import { Component, signal } from '@angular/core';
import { RouterLink, RouterLinkActive } from '@angular/router';

import { environment } from '../../../environments/environment';
import { RUTAS_BACKOFFICE } from '../../../environments/backoffice';
import { Icono } from '../icono/icono';

/**
 * Header de la web pública (Figma: componente `header`, user=anonimo).
 * Base oscura con ondas de gradiente animado y logo. Desktop: menú inline
 * (Home · Buscador de propiedades · Contacto · Ingreso Brokers). Mobile
 * (Figma 157-762): hamburguesa que despliega el menú.
 *
 * El fondo está portado del header del backoffice, que es la versión más
 * reciente del diseño: base `#12031d` + `bg_7.png` + ondas cuyo gradiente
 * cicla ocho colores en 24s. Difiere del frame `244:3021` del Figma público,
 * que todavía muestra el gradiente magenta.
 *
 * El bloque del SVG va copiado tal cual de
 * `frontend-backoffice/src/components/header/header.component.html`, sin
 * bindings ni bloques de control: SMIL inicializa los `<animate>` a partir de
 * sus atributos al insertarlos, así que un `values` enlazado (que Angular
 * asigna después) puede dejar la animación sin arrancar. Para el movimiento
 * reducido se oculta la capa por CSS, que no depende de tocar el SVG.
 *
 * Decisión D3: no hay sesión en el portal, sólo la variante `user=anonimo`
 * del set `header` (`244:3021`). Por eso tampoco va la campana de
 * notificaciones del Figma mobile (FP-05 / decisión D2).
 *
 * Ingreso Brokers es un link externo al backoffice: es una pantalla de la otra
 * aplicación (FP-04). Contacto sí es una pantalla del portal desde FP-40, que
 * dio de baja la redirección que pedía la decisión D5.
 *
 * RP-12 sacó Registro Brokers del menú, en desktop y en mobile: el alta de
 * brokers no se ofrece desde el portal. Es el único ítem que se dio de baja,
 * por eso lo que queda es un link solo y no la lista que había antes.
 *
 * Nota DP08: el header NO incluye buscador global (no existe en el Figma).
 * La integración del término `buscar` con el mapa vive en la página del
 * buscador, que reacciona a `?buscar=` en la URL.
 */
@Component({
  selector: 'app-header',
  imports: [RouterLink, RouterLinkActive, Icono],
  template: `
    <header class="header">
    <div class="header__fondo" aria-hidden="true">
      <svg
        class="header__ondas"
        xmlns="http://www.w3.org/2000/svg"
        xmlns:xlink="http://www.w3.org/1999/xlink"
        width="100%"
        height="100%"
        viewBox="0 0 1512 96"
        preserveAspectRatio="none"
      >
        <path
          d="M1523 -0.000488281H1331.93C1280.51 58.7327 1214.07 93.6775 1129.02 87.7863C1032.88 80.9226 930.421 28.2706 830.813 -0.000488281H-11V83.7061C72.5786 111.195 166.009 86.7914 259.154 62.3882C323.031 45.6536 386.775 28.919 447.11 28.919C449.88 28.919 452.66 28.9525 455.419 29.0196C529.065 31.11 586.945 62.3099 632.032 111.809H1523V-0.000488281Z"
          fill="url(#paint0_linear_577_501)"
        />
        <path d="M-11 111.821V112H632.194L632.032 111.821H-11Z" fill="url(#paint1_linear_577_501)" />
        <path
          d="M447.12 28.9301C386.785 28.9301 323.041 45.6647 259.165 62.3994C166.009 86.8026 72.5786 111.206 -11 83.7061V111.809H632.022C586.945 62.3099 529.055 31.11 455.409 29.0196C452.649 28.9413 449.87 28.919 447.1 28.919L447.12 28.9301Z"
          fill="url(#paint2_linear_577_501)"
        />
        <path
          d="M1331.93 -0.000488281H830.813C930.411 28.2706 1032.88 80.9337 1129.02 87.7863C1214.07 93.6663 1280.51 58.7215 1331.93 -0.000488281Z"
          fill="url(#paint4_linear_577_501)"
        />

        <defs>
          <linearGradient id="stopsA" gradientUnits="userSpaceOnUse">
            <stop offset="0" stop-color="#C19E06">
              <animate
                attributeName="stop-color"
                dur="24s"
                calcMode="linear"
                repeatCount="indefinite"
                values="#C19E06;#B4CC20;#2F9435;#00CFBD;#2684D9;#7C4695;#E21F51;#ED6E1B;#C19E06"
              />
            </stop>
            <stop offset="0.3333" stop-color="#B4CC20">
              <animate
                attributeName="stop-color"
                dur="24s"
                calcMode="linear"
                repeatCount="indefinite"
                values="#B4CC20;#2F9435;#00CFBD;#2684D9;#7C4695;#E21F51;#ED6E1B;#C19E06;#B4CC20"
              />
            </stop>
            <stop offset="0.6667" stop-color="#2F9435">
              <animate
                attributeName="stop-color"
                dur="24s"
                calcMode="linear"
                repeatCount="indefinite"
                values="#2F9435;#00CFBD;#2684D9;#7C4695;#E21F51;#ED6E1B;#C19E06;#B4CC20;#2F9435"
              />
            </stop>
            <stop offset="1" stop-color="#00CFBD">
              <animate
                attributeName="stop-color"
                dur="24s"
                calcMode="linear"
                repeatCount="indefinite"
                values="#00CFBD;#2684D9;#7C4695;#E21F51;#ED6E1B;#C19E06;#B4CC20;#2F9435;#00CFBD"
              />
            </stop>
          </linearGradient>
          <linearGradient
            id="paint0_linear_577_501"
            x1="1217.24"
            y1="-453.084"
            x2="205.085"
            y2="463.963"
            gradientUnits="userSpaceOnUse"
            href="#stopsA"
            xlink:href="#stopsA"
          />
          <linearGradient
            id="paint1_linear_577_501"
            x1="-11"
            y1="111.9"
            x2="632"
            y2="111.9"
            gradientUnits="userSpaceOnUse"
            href="#stopsA"
            xlink:href="#stopsA"
          />
          <linearGradient
            id="paint2_linear_577_501"
            x1="2433.04"
            y1="-953.564"
            x2="1200.64"
            y2="1021.22"
            gradientUnits="userSpaceOnUse"
            href="#stopsA"
            xlink:href="#stopsA"
          />
          <linearGradient
            id="paint4_linear_577_501"
            x1="1592.32"
            y1="-381.22"
            x2="621.518"
            y2="1748.25"
            gradientUnits="userSpaceOnUse"
            href="#stopsA"
            xlink:href="#stopsA"
          />
        </defs>
      </svg>
    </div>

    <a routerLink="/" class="logo" aria-label="Phindi — inicio">
      <img src="/logos/logo-grey-25.svg" alt="Phindi" width="143" height="48" />
    </a>

    <div class="header__derecha">
      <!-- Solo mobile: hamburguesa (Figma 157-762) -->
      <!--
        Las tres barras van como elementos y no como icono: así se transforman
        en cruz con una transición, igual que el "hamburger-btn" del backoffice.
      -->
      <button
        type="button"
        class="icono-btn"
        [class.icono-btn--activo]="menuAbierto()"
        (click)="menuAbierto.set(!menuAbierto())"
        [attr.aria-expanded]="menuAbierto()"
        [attr.aria-label]="menuAbierto() ? 'Cerrar menú' : 'Menú'"
      >
        <span></span>
        <span></span>
        <span></span>
      </button>

      <nav
        class="menu"
        [class.menu--abierto]="menuAbierto()"
        aria-label="Navegación principal"
      >
        <a
          routerLink="/"
          class="menu__item"
          routerLinkActive="menu__item--activo"
          [routerLinkActiveOptions]="{ exact: true }"
          (click)="menuAbierto.set(false)"
          >Home</a
        >
        <a
          routerLink="/buscador"
          class="menu__item"
          routerLinkActive="menu__item--activo"
          (click)="menuAbierto.set(false)"
          >Buscador de propiedades</a
        >
        <a
          routerLink="/contacto"
          class="menu__item"
          routerLinkActive="menu__item--activo"
          (click)="menuAbierto.set(false)"
          >Contacto</a
        >
        <!--
          Link externo al backoffice (FP-04). Abre en pestaña nueva: es otra
          aplicación, y así no se pierde la búsqueda en curso.
        -->
        <a
          [href]="linkIngresoBrokers"
          class="menu__item"
          target="_blank"
          rel="noopener"
          (click)="menuAbierto.set(false)"
          >Ingreso Brokers</a
        >
      </nav>
    </div>
    </header>
  `,
  styles: `
    .header {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      z-index: 100;
      height: 96px;
      padding: 24px 48px;
      display: flex;
      align-items: center;
      justify-content: space-between;
      /* Base oscura del backoffice; las ondas van encima. */
      background:
        url('/backgrounds/bg_7.png') center / cover no-repeat,
        var(--c-fondo-header);
      box-shadow: var(--elevation-5);
    }

    .header__fondo {
      position: absolute;
      inset: 0;
      overflow: hidden;
      pointer-events: none;
    }

    .header__ondas {
      display: block;
      width: 100%;
      height: 100%;
    }

    /* El gradiente cicla indefinidamente: con movimiento reducido se oculta
       la capa y queda la base oscura sola. SMIL no se puede frenar por CSS,
       pero sí sacar de la vista. */
    @media (prefers-reduced-motion: reduce) {
      .header__fondo {
        display: none;
      }
    }

    /* El contenido va por encima de las ondas.
       Sin "position: relative" a propósito: los dos son flex items del header,
       y en un flex item el z-index aplica igual estando en "static". Con
       "relative", .header__derecha pasaba a ser el bloque contenedor del menú
       desplegable, que mide lo que el botón de hamburguesa (40 px), y el panel
       de mobile salía de ese ancho con las etiquetas fuera de pantalla. */
    .logo,
    .header__derecha {
      z-index: 1;
    }

    .logo img {
      display: block;
    }

    .header__derecha {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    /* Hamburguesa que se convierte en cruz. Portado del "hamburger-btn" del
       backoffice: mismas medidas de barra y mismos transforms, con el color
       heredado del header en vez del rose_50 de allá. */
    .icono-btn {
      display: none;
      width: 40px;
      height: 40px;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 6px;
      border: none;
      border-radius: 8px;
      background: none;
      color: var(--c-neutrals-grey_0);
      padding: 0;
      cursor: pointer;
      transition: background 0.3s ease;

      span {
        display: block;
        width: 20px;
        height: 2px;
        border-radius: 1px;
        background: currentColor;
        transition: transform 0.3s ease, opacity 0.3s ease;
      }

      &:hover {
        background: rgba(255, 255, 255, 0.12);
      }

      &:active {
        transform: scale(0.95);
      }
    }

    /* La barra de arriba y la de abajo giran para formar la cruz; la del
       medio se desvanece. */
    .icono-btn--activo span:first-child {
      transform: rotate(45deg) translate(7px, 5px);
    }

    .icono-btn--activo span:nth-child(2) {
      opacity: 0;
    }

    .icono-btn--activo span:last-child {
      transform: rotate(-45deg) translate(6px, -5px);
    }

    /* Con movimiento reducido el estado cambia igual, pero sin recorrido. */
    @media (prefers-reduced-motion: reduce) {
      .icono-btn,
      .icono-btn span {
        transition: none;
      }
    }

    .menu {
      display: flex;
      align-items: center;
      gap: 10px;
    }

    .menu__item {
      padding: 12px;
      border-radius: 99999px;
      font-size: 16px;
      font-weight: var(--fw-semibold);
      line-height: 16px;
      color: var(--c-phindi-rose_50);
      white-space: nowrap;
    }

    .menu__item--activo {
      border: 1px solid var(--c-phindi-rose_50);
    }

    .menu__item:hover {
      background: rgba(255, 255, 255, 0.12);
    }

    /* ── Mobile (Figma 157-762) ───────────────────────────────────────────── */
    @media (max-width: 900px) {
      .header {
        height: 64px;
        padding: 12px 16px;
      }

      .logo img {
        width: 96px;
        height: 32px;
      }

      .icono-btn {
        display: flex;
      }

      /* Hoja desplegable a todo el ancho, anclada al borde inferior del
         header. */
      .menu {
        display: none;
        position: absolute;
        top: 100%;
        right: 0;
        left: 0;
        flex-direction: column;
        align-items: stretch;
        gap: 0;
        padding: 0;
        background: var(--c-neutrals-grey_25);
        border-radius: 0 0 var(--br-m) var(--br-m);
        box-shadow: var(--elevation-3);
        /* Recorta el fondo de los ítems contra las esquinas redondeadas */
        overflow: hidden;
      }

      .menu--abierto {
        display: flex;
      }

      .menu__item {
        padding: var(--pad-l) var(--pad-xl);
        border-radius: 0;
        color: var(--c-neutrals-grey_600);
      }

      .menu__item--activo {
        border: none;
      }

      .menu__item:hover {
        background: var(--c-neutrals-grey_50);
      }
    }
  `,
})
export class Header {
  readonly menuAbierto = signal(false);

  /**
   * Único ítem del menú que sale del portal hacia el backoffice (FP-04). La URL
   * base sale de `environment` para que no quede quemada en el template y cambie
   * sola por entorno.
   */
  readonly linkIngresoBrokers =
    environment.backofficeUrl + RUTAS_BACKOFFICE.ingresoBrokers;
}
